import React, { useEffect } from "react";
import classes from "./SingleDatesWidget.module.scss";
import { IntSingleDate as SingleDate } from "models/Ndtrc";
import { useFieldArray } from "react-hook-form";
import { registerLocale } from "react-datepicker";
import nl from "date-fns/locale/nl";
import { v4 as uuidv4 } from "uuid";

import "react-datepicker/dist/react-datepicker.css";
import { TiDelete } from "react-icons/ti";
import { RiDeleteBin3Line } from "react-icons/ri";
import { IoMdAddCircle } from "react-icons/io";

import { useTranslation } from "react-i18next";
import { DateWidgetWrapper } from "components/FormWidgets/DateWidget";
import WhenWidget from "components/FormWidgets/CalendarWidget/WhenWidget";
import { SingleSelectOption } from "models/SelectOption";

const generateDefaultEvent = (): SingleDate => {
  return {
    date: null, // new Date().toISOString(),
    when: [{ timestart: null, timeend: null }], // "12:00"
    id: uuidv4(),
  };
};

const SingleDatesWidget = (props: {
  formMethods: any;
  namespace: string;
  hideWhenStatus?: boolean;
  overrideSingleDateOptions?: SingleSelectOption[];
  endTimesRequired?: boolean;
}) => {
  const { t } = useTranslation();
  const control: any = props.formMethods.control; // required, as field array does not like input via props
  const { fields, append, prepend, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: `${props.namespace}`, // unique name for your Field Array
    keyName: "id", // default to "id", you can change the key name
  });

  registerLocale("nl", nl);

  useEffect(() => {
    if (!(fields && fields.length > 0)) {
      append(generateDefaultEvent());
    }
  }, []);

  // console.log(props.formMethods.formState.errors); // DEBUG

  return (
    <div className={classes.CalendarWidget}>
      {fields.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>{t("calendarWidget.date")}</th>
              <th>
                {props.hideWhenStatus
                  ? t("calendarWidget.time")
                  : t("calendarWidget.timeAndStatus")}
              </th>
              <th>
                <div
                  className={`${classes.deleteAll} ff-delete-all`}
                  onClick={() => {
                    remove();
                  }}
                >
                  <div>
                    <RiDeleteBin3Line />
                    <span>{t("calendarWidget.deleteAll")}</span>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {
              // @ts-ignore // Type SingleDate not allowed as description, but is correct
              fields.map((singleDate: SingleDate, index) => {
                // console.log(singleDate); // DEBUG
                return (
                  <tr key={singleDate.id}>
                    <td style={{ verticalAlign: "top" }}>
                      <DateWidgetWrapper
                        name={`${props.namespace}[${index}].date`}
                        formMethods={props.formMethods}
                        defaultValue={singleDate.date}
                        hasError={
                          props.formMethods.formState.errors.ndtrcEntry
                            ?.calendar?.singleDates[index]?.date
                        }
                      />
                    </td>
                    <td style={{ verticalAlign: "top" }}>
                      <WhenWidget
                        defaultValue={singleDate.when?.[0]}
                        formMethods={props.formMethods}
                        namespace={`${props.namespace}[${index}].when[0]`}
                        noStatus={props.hideWhenStatus}
                        startTimeRequired={true}
                        endTimeRequired={props.endTimesRequired}
                        overrideSingleDateOptions={
                          props?.overrideSingleDateOptions
                        }
                      />
                    </td>
                    <td>
                      <div
                        onClick={() => remove(index)}
                        className={classes.danger}
                      >
                        <TiDelete />
                      </div>
                    </td>
                    {props.formMethods.formState.errors.ndtrcEntry?.calendar
                      ?.singleDates[index] && (
                      <td>
                        <div className={classes.errorInformation}>
                          {[
                            props.formMethods.formState.errors.ndtrcEntry
                              ?.calendar?.singleDates[index]?.date &&
                              t("calendarWidget.dateRequiredError"),
                            props.formMethods.formState.errors.ndtrcEntry
                              ?.calendar?.singleDates[index]?.when?.[0]
                              ?.timestart &&
                              t("calendarWidget.timestartRequiredError"),
                            props.formMethods.formState.errors.ndtrcEntry
                              ?.calendar?.singleDates[index]?.when?.[0]
                              ?.timeend &&
                              t("calendarWidget.timeendRequiredError"),
                          ]
                            .filter((item: any) => item)
                            .join(" & ")}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      )}
      <div
        onClick={() => {
          append(generateDefaultEvent());
        }}
        className={classes.add}
      >
        <IoMdAddCircle />
      </div>
    </div>
  );
};

export default SingleDatesWidget;
